import pluralize from 'pluralize';

/**
 * Adding terms to the user's "foods to avoid" list (familyProfile.dislikes).
 *
 * Extracted from RecipeDetailsView so the swap sheet can offer the same thing
 * without a second copy of the merge-and-PATCH dance. Two surfaces now write
 * this list, and they must agree on deduplication or a user ends up with both
 * "green bean" and "green beans" quietly failing to match recipes.
 *
 * The profile is re-fetched rather than taken from local state: PATCH replaces
 * the whole array, so merging against a stale copy would silently drop
 * whatever was added elsewhere in the meantime.
 */

/** Matches how the preferences UI cases terms the user types. */
export const toFoodsToAvoidTerm = (term: string): string => {
  const trimmed = term.trim();
  if (!trimmed) return trimmed;
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
};

/**
 * Singular/plural-insensitive membership test, matching the comparison
 * RecipeDetailsView already used so the two surfaces can't disagree about
 * whether something is already on the list.
 */
export const isAlreadyAvoided = (
  term: string,
  existing: string[]
): boolean => {
  const normalized = term.toLowerCase().trim();
  const singularForm = pluralize.singular(normalized);
  const pluralForm = pluralize.plural(normalized);

  return existing.some((entry) => {
    const normalizedEntry = entry.toLowerCase().trim();
    return (
      normalizedEntry === normalized ||
      pluralize.singular(normalizedEntry) === singularForm ||
      pluralize.plural(normalizedEntry) === pluralForm
    );
  });
};

/**
 * Merges `terms` into the user's foods-to-avoid list.
 *
 * Returns the terms that were actually new plus the resulting list, so callers
 * can refresh local state and decide whether to say anything to the user. A
 * no-op (everything already present) resolves with an empty `added`, never an
 * error — re-adding something you already avoid is not a failure.
 */
export const addFoodsToAvoid = async (
  idToken: string,
  terms: string[],
  apiUrl = process.env.REACT_APP_API_URL
): Promise<{ added: string[]; dislikes: string[] }> => {
  const profileResponse = await fetch(`${apiUrl}/api/user/profile`, {
    headers: { Authorization: `Bearer ${idToken}` },
  });
  if (!profileResponse.ok) {
    throw new Error('Failed to fetch profile');
  }
  const profileData = await profileResponse.json();
  const currentDislikes: string[] =
    profileData.user?.familyProfile?.dislikes ?? [];

  const added: string[] = [];
  const next = [...currentDislikes];
  for (const raw of terms) {
    const term = toFoodsToAvoidTerm(raw);
    if (!term) continue;
    // Checked against `next`, not `currentDislikes`, so two spellings of the
    // same food in one batch can't both land.
    if (isAlreadyAvoided(term, next)) continue;
    next.push(term);
    added.push(term);
  }

  if (added.length === 0) {
    return { added: [], dislikes: currentDislikes };
  }

  const updateResponse = await fetch(`${apiUrl}/api/user/family-profile`, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ dislikes: next }),
  });
  if (!updateResponse.ok) {
    const data = await updateResponse.json().catch(() => ({}));
    throw new Error(data.error || 'Failed to update profile');
  }

  return { added, dislikes: next };
};
